import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Store,
  BarChart4,
  Activity,
  Truck,
  Ambulance,
  MapPin,
  BrainCircuit,
  Megaphone,
  Mail,
  MessageCircle,
  Percent,
  Tag,
  ClipboardList,
  Banknote,
  FileText,
  MessageSquare,
  ShieldAlert,
  Settings,
  HelpCircle,
  BookOpen,
  History,
  Stethoscope,
  Link2,
  Phone,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Smartphone,
  Film,
  X,
  HeadphonesIcon,
  LineChart,
  Server,
  PieChart,
  Cloud,
  BadgeCheck,
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
}

interface NavSection {
  id: string;
  label: string;
  /** Short hint shown under label when expanded */
  description?: string;
  items: NavItem[];
}

interface SidebarProps {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const ICON = 'w-[18px] h-[18px]';

function pathIsActive(itemPath: string, pathname: string): boolean {
  if (itemPath === '/master-admin') {
    return pathname === '/master-admin' || pathname === '/';
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function sectionHasActiveItem(section: NavSection, pathname: string): boolean {
  return section.items.some((item) => pathIsActive(item.path, pathname));
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onCloseMobile }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const sections: NavSection[] = useMemo(
    () => [
      {
        id: 'dashboard',
        label: 'Dashboard',
        items: [
          {
            title: 'Home',
            path: '/master-admin',
            icon: <LayoutDashboard className={ICON} />,
          },
        ],
      },
      {
        id: 'people',
        label: 'People & Directory',
        description: 'Vendors, users, team access',
        items: [
          { title: 'Vendors', path: '/vendors', icon: <Store className={ICON} /> },
          { title: 'Users', path: '/users', icon: <Users className={ICON} /> },
          { title: 'Team Access', path: '/employees', icon: <Users className={ICON} /> },
          { title: 'Vendor form catalog', path: '/vendor-form-catalog', icon: <BookOpen className={ICON} /> },
          { title: 'Profile update history', path: '/vendor-profile-updates', icon: <History className={ICON} /> },
        ],
      },
      {
        id: 'analytics',
        label: 'Analytics & Monitoring',
        description: 'Reports, tracking, storage',
        items: [
          { title: 'Analytics dashboard', path: '/insights/analytics-dashboard', icon: <LineChart className={ICON} /> },
          { title: 'Her Phases Analytics', path: '/insights/her-phases-analytics', icon: <Activity className={ICON} /> },
          { title: 'App tracking insights', path: '/insights/app-tracking', icon: <Activity className={ICON} /> },
          { title: 'Data monitoring', path: '/monitoring', icon: <Activity className={ICON} /> },
          { title: 'Production operations', path: '/operations/production-dashboard', icon: <Server className={ICON} /> },
          { title: 'API & error report', path: '/reports', icon: <BarChart4 className={ICON} /> },
          { title: 'Chatbot intelligence', path: '/chatbot-intelligence', icon: <BrainCircuit className={ICON} /> },
          { title: 'User storage', path: '/health-record-usage', icon: <BarChart4 className={ICON} /> },
          { title: 'Vendor storage', path: '/vendor-storage', icon: <Store className={ICON} /> },
        ],
      },
      {
        id: 'billing',
        label: 'Billing Analytics',
        description: 'Cloud cost and usage tracking',
        items: [
          { title: 'Overview', path: '/billing/overview', icon: <PieChart className={ICON} /> },
          { title: 'Services', path: '/billing/services', icon: <Cloud className={ICON} /> },
          { title: 'Resources', path: '/billing/resources', icon: <Server className={ICON} /> },
          { title: 'Billing History', path: '/billing/history', icon: <History className={ICON} /> },
        ],
      },
      {
        id: 'support',
        label: 'Customer Support',
        description: 'Feedback, grievances, FAQs',
        items: [
          { title: 'Feedbacks', path: '/feedbacks', icon: <MessageSquare className={ICON} /> },
          { title: 'Grievances', path: '/grievances', icon: <ShieldAlert className={ICON} /> },
          { title: 'Support FAQs', path: '/insights/support-faqs', icon: <HelpCircle className={ICON} /> },
        ],
      },
      {
        id: 'fulfillment',
        label: 'Orders & Fulfillment',
        description: 'Queues, bookings, deliveries, refunds',
        items: [
          { title: 'Request queue', path: '/request-queue', icon: <ClipboardList className={ICON} /> },
          {
            title: 'Ambulance bookings',
            path: '/ambulance-bookings',
            icon: <Ambulance className={ICON} />,
          },
          { title: 'Borzo deliveries', path: '/deliveries/borzo', icon: <Truck className={ICON} /> },
          {
            title: 'Memberships',
            path: '/operations/membership',
            icon: <BadgeCheck className={ICON} />,
          },
          {
            title: 'Manual refund cases',
            path: '/master-admin/operations/manual-refund-cases',
            icon: <Banknote className={ICON} />,
          },
        ],
      },
      {
        id: 'marketing',
        label: 'Marketing & Outreach',
        description: 'Push, email, WhatsApp, calls',
        items: [
          { title: 'Push notifications', path: '/push-notifications', icon: <Megaphone className={ICON} /> },
          { title: 'Marketing emails', path: '/operations/marketing-emails', icon: <Mail className={ICON} /> },
          { title: 'WhatsApp', path: '/operations/whatsapp', icon: <MessageCircle className={ICON} /> },
          { title: 'Exotel Calling', path: '/operations/exotel-calling', icon: <Phone className={ICON} /> },
          { title: 'Offers & discounts', path: '/operations/booking-offers', icon: <Tag className={ICON} /> },
          { title: 'Deep links', path: '/operations/deep-links', icon: <Link2 className={ICON} /> },
          { title: 'Media library', path: '/operations/media-management', icon: <Film className={ICON} /> },
        ],
      },
      {
        id: 'vendor-ops',
        label: 'Vendor Operations',
        description: 'Commission, policies, queries',
        items: [
          {
            title: 'Commission management',
            path: '/operations/vendor-commission-management',
            icon: <Percent className={ICON} />,
          },
          { title: 'Policy queries', path: '/operations/vendor-policy-queries', icon: <MessageSquare className={ICON} /> },
          { title: 'Contact queries', path: '/operations/vendor-contact-queries', icon: <HeadphonesIcon className={ICON} /> },
          { title: 'Legal & policy versions', path: '/operations/legal-policies', icon: <FileText className={ICON} /> },
        ],
      },
      {
        id: 'clinical',
        label: 'Clinical & Ambulance',
        description: 'Consultation & ambulance config',
        items: [
          {
            title: 'Instant consultation specialities',
            path: '/operations/instant-consultation/specialities',
            icon: <Stethoscope className={ICON} />,
          },
          {
            title: 'Symptom speciality mappings',
            path: '/operations/instant-consultation/symptom-mappings',
            icon: <BrainCircuit className={ICON} /> },
          { title: 'Ambulance service areas', path: '/ambulance-service-areas', icon: <MapPin className={ICON} /> },
          { title: 'Off-hours ambulance demand', path: '/ambulance-off-hours-demand', icon: <Ambulance className={ICON} /> },
        ],
      },
      {
        id: 'product',
        label: 'Product & App',
        description: 'App releases & version control',
        items: [
          { title: 'App version control', path: '/app-version-control', icon: <Smartphone className={ICON} /> },
        ],
      },
      {
        id: 'system',
        label: 'System',
        description: 'Settings & help',
        items: [
          { title: 'Settings', path: '/settings', icon: <Settings className={ICON} /> },
          { title: 'Help & support', path: '/help', icon: <HelpCircle className={ICON} /> },
        ],
      },
    ],
    []
  );

  const activeSectionId = useMemo(() => {
    const match = sections.find((section) => sectionHasActiveItem(section, location.pathname));
    return match?.id ?? null;
  }, [sections, location.pathname]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeSectionId) return;
    setOpenSections((prev) => ({ ...prev, [activeSectionId]: true }));
  }, [activeSectionId]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderLink = (item: NavItem) => {
    const active = pathIsActive(item.path, location.pathname);
    return (
      <Link
        to={item.path}
        onClick={() => onCloseMobile?.()}
        title={collapsed ? item.title : undefined}
        className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
          collapsed ? 'max-lg:justify-start lg:mx-1 lg:justify-center lg:px-2' : 'mx-2'
        } ${
          active
            ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
        }`}
      >
        <span className="shrink-0">{item.icon}</span>
        {!collapsed ? (
          <span className="min-w-0 truncate">{item.title}</span>
        ) : (
          <span className="inline lg:hidden">{item.title}</span>
        )}
      </Link>
    );
  };

  const renderExpandedSection = (section: NavSection) => {
    const isSingle = section.items.length === 1;
    const isOpen = openSections[section.id] ?? false;
    const isActiveSection = section.id === activeSectionId;

    if (isSingle) {
      return (
        <div key={section.id} className="py-1">
          <ul>
            <li>{renderLink(section.items[0])}</li>
          </ul>
        </div>
      );
    }

    return (
      <div key={section.id} className="py-0.5">
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          className={`flex w-full items-start justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
            isActiveSection && !isOpen ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
          }`}
          aria-expanded={isOpen}
        >
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-semibold text-gray-800 dark:text-gray-100">{section.label}</span>
            {section.description && (
              <span className="mt-0.5 block truncate text-[10px] text-gray-400 dark:text-gray-500">
                {section.description}
              </span>
            )}
          </span>
          <ChevronDown
            className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-0' : '-rotate-90'
            }`}
          />
        </button>
        {isOpen && (
          <ul className="space-y-0.5 pb-2">
            {section.items.map((item) => (
              <li key={item.path}>{renderLink(item)}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderCollapsedSection = (section: NavSection, showDivider: boolean) => (
    <div
      key={section.id}
      className={showDivider ? 'mt-1 border-t border-gray-100 pt-1 dark:border-gray-700/80' : ''}
    >
      <ul className="space-y-0.5">
        {section.items.map((item) => (
          <li key={item.path}>{renderLink(item)}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex h-screen w-[min(16rem,88vw)] flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800 lg:static lg:z-auto lg:border-r-0 lg:shadow-none lg:transition-[width] lg:duration-300 ${
        mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full lg:translate-x-0'
      } ${collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'}`}
      aria-label="Main navigation"
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3 py-3 dark:border-gray-700">
        <div
          className={`min-w-0 truncate font-semibold text-base text-primary-600 dark:text-primary-400 ${
            collapsed ? 'lg:hidden' : ''
          }`}
        >
          Vedika Admin
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => onCloseMobile?.()}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden rounded-md bg-gray-100 p-1.5 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 lg:inline-flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 scrollbar-thin">
        {collapsed ? (
          <div>{sections.map((section, idx) => renderCollapsedSection(section, idx > 0))}</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {sections.map((section) => renderExpandedSection(section))}
          </div>
        )}
      </nav>

      <div className="flex flex-shrink-0 items-center border-t border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => {
            onCloseMobile?.();
            void handleLogout();
          }}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-red-600 dark:text-gray-300 dark:hover:bg-gray-700/50 dark:hover:text-red-400 ${
            collapsed ? 'max-lg:justify-start lg:justify-center' : ''
          }`}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed ? <span>Logout</span> : <span className="inline lg:hidden">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
